import { isEmpty } from "psims/lib/empty";
import { recordActionFromEnum } from "psims/models/api/data-submission-record-action";
import { AustralianControllingCorporation } from "../types";

const CHAR_LIMIT = 200;
const ENTITY_NAME_REGEX = /^[a-zA-Z0-9\-'(), ]+$/;
const NUMERIC_REGEX = /^\d+$/;

function hasInvalidCharacters(value: string | undefined | null): boolean {
  if (!value) return false;
  return !ENTITY_NAME_REGEX.test(value);
}

export type ACCValidationField =
  | 'newCorporationName'
  | 'abn'
  | 'acn'
  | 'address'
  | 'contactInformation'
  | 'comments';

export type ACCValidationResult = {
  key: ACCValidationField;
} & (
  | { code: 'invalid_required' }
  | { code: 'character_limit'; limit: number }
  | { code: 'invalid_characters' }
  | { code: 'invalid_length'; requiredLength: number }
  | { code: 'invalid_abn_acn_format'; requiredLength: number }
  | { code: 'numeric_only' }
  | { code: 'abn_or_acn_required' }
);

export function validateAustralianControllingCorporation(
  record: AustralianControllingCorporation,
): Array<ACCValidationResult> {
  const messages: Array<ACCValidationResult> = [];

  if (recordActionFromEnum(record.recordAction) === 'Delete') {
    return messages;
  }

  if (!record.hasChangedInPreviousYear) {
    return messages;
  }

  function validateTextField(
    key: ACCValidationField,
    value: string | undefined | null,
  ) {
    if (isEmpty(value)) {
      messages.push({ key, code: 'invalid_required' });
    } else if ((value?.length || 0) > CHAR_LIMIT) {
      messages.push({ key, code: 'character_limit', limit: CHAR_LIMIT });
    } else if (hasInvalidCharacters(value)) {
      messages.push({ key, code: 'invalid_characters' });
    }
  }

  function validateOptionalTextFieldForInavlidCharacters(
    key: ACCValidationField,
    value: string | undefined | null,
  ) {
    if (hasInvalidCharacters(value)) {
      messages.push({ key, code: 'invalid_characters' });
    }
  }

  // Validates format/length only — does NOT push invalid_required.
  // Used when the field is optional but must be correct if provided.
  function validateOptionalFixedLengthNumericField(
    key: ACCValidationField,
    value: string | undefined | null,
    requiredLength: number,
  ) {
    if (isEmpty(value)) return;
    if (!NUMERIC_REGEX.test(value!) || value!.length !== requiredLength) {
      messages.push({ key, code: 'invalid_abn_acn_format', requiredLength });
    }
  }

  // --- ABN / ACN precedence validation ---
  const abnProvided = !isEmpty(record.abn);
  const acnProvided = !isEmpty(record.acn);

  if (!abnProvided && !acnProvided) {
    // Neither entered — flag ABN as the preferred field
    messages.push({ key: 'abn', code: 'abn_or_acn_required' });
  } else {
    // Validate whichever fields are present for format correctness
    if (abnProvided) {
      validateOptionalFixedLengthNumericField('abn', record.abn, 11);
    }
    if (acnProvided) {
      validateOptionalFixedLengthNumericField('acn', record.acn, 9);
    }
    // If only ACN is provided, ABN is satisfied — no error on ABN
  }

  validateTextField('newCorporationName', record.newCorporationName);
  validateTextField('address', record.address);
  validateTextField('contactInformation', record.contactInformation);
  validateOptionalTextFieldForInavlidCharacters('comments', record.comments);

  return messages;
}